import { Component, Input, OnInit } from '@angular/core';
import { BaseEntityEvent } from '@memberjunction/core';
import { BillingCycleEntity, SubscriptionEntityExtended } from 'mj_generatedentities';
import { UserContextService } from 'src/app/core/services/user-context.service';
import { PlanCard, SubscriptionPlanService } from 'src/app/core/services/subscription-plan.service';

interface PlanCardWrapper extends PlanCard {
  isHigherTier?: boolean; // Optional property to indicate if the plan is a higher tier
  canSwitch?: boolean; // Optional property to indicate if the plan can be switched to
  isCurrent?: boolean; // Optional property to indicate if the plan is the current one
}

@Component({
  selector: 'app-price-table',
  templateUrl: './price-table.component.html',
  styleUrl: './price-table.component.css'
})
export class PriceTableComponent implements OnInit{
  @Input() subscriptionID: string;

  /**
   * If true, visually highlight plans that are higher than the current plan
   * (thicker blue border, for example).
   */
  @Input() highlightHigherTiers = false;

  subscription: SubscriptionEntityExtended;

  subscriptions: PlanCardWrapper[] = [];
  onSelectionCallback: (sub: PlanCard, cycleID: string) => Promise<void>;

  billingPeriods: BillingCycleEntity[];
  selectedBillingPeriod: BillingCycleEntity;

  inOnboarding = false;

  constructor(
    private subscriptionService: SubscriptionPlanService, 
    private userContextService: UserContextService
  ) {
    this.userContextService.OnboardingState$.subscribe((state) => {
      this.inOnboarding = state === 'Ongoing' || state === 'Preboarding';
    });
    this.subscription = this.userContextService.Subscriptions.find((sub) => sub.ID === this.subscriptionID);
  }

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  async loadData(): Promise<void> {
    this.billingPeriods = this.subscriptionService.BillingPeriods;

    if (this.subscription) {
      const plans = this.subscriptionService.Plans;
      const price = plans.flatMap((plan) => plan.Prices).find((price) => price.ID === this.subscription.PlanPriceID);
      this.selectedBillingPeriod = this.billingPeriods.find((period) => period.ID === price.BillingCycleID) || this.billingPeriods[0];
    } else {
      this.selectedBillingPeriod = this.billingPeriods.find((period) => period.Name === 'Montly') || this.billingPeriods[0]; // By default select monthly
    }

    const planCards = this.subscriptionService.PlanCards;
    this.subscriptions = await Promise.all(planCards.map(async (plan) =>  {
      const isHigherTier = this.highlightHigherTiers && this.subscription ? await this.isHigherTier(plan) : false;
      const canSwitch = await this.canSwitchPlan(plan);
      const isCurrent = await this.isCurrentPlan(plan);
      return { ...plan, isHigherTier, canSwitch, isCurrent } as PlanCardWrapper;
    }));
  }

  switchBillingPeriod(period: BillingCycleEntity): void {
    this.selectedBillingPeriod = period;
  }

  async isCurrentPlan(sub: PlanCard): Promise<boolean> {
    if (!this.inOnboarding && this.subscription) {
      const PriceID = sub.PriceData.get(this.selectedBillingPeriod.ID).PriceID;
      const currentPlan = await this.subscription.GetPlan();
      return currentPlan.ID === sub.ID && this.subscription.PlanPriceID === PriceID;
    }
    return false;
  }

  /**
   * Checks if `sub` is a higher tier than the user's current subscription.
   * You might store a rank or tierLevel on each plan or compare features.
   */
  private async isHigherTier(sub: PlanCard): Promise<boolean> {
    if (!this.subscription) return false;
    const currentPlan = await this.subscription.GetPlan();
    const subPlan = this.subscriptionService.Plans.find((plan) => plan.Plan.ID === sub.ID).Plan;

    if (!currentPlan?.Sequence || !subPlan?.Sequence) return false;

    return subPlan.Sequence > currentPlan.Sequence;
  }

  /**
   * Determines if the user can switch to the given plan (i.e., usage doesn't exceed plan constraints).
   * For instance, if the plan allows up to X users, but the user has X+1, return false.
   */
  private async canSwitchPlan(sub: PlanCard): Promise<boolean> {
    if (this.inOnboarding) {
      return true; // Typically no usage constraints during onboarding, or adjust logic as needed
    }

    if (!this.subscription) return false;

    const priceID = sub.PriceData.get(this.selectedBillingPeriod.ID).PriceID;
    const result = await this.subscription.ValidatePlanPriceChange(priceID);

    return result.IsValid;
  }

  getButtonText(sub: PlanCardWrapper): string {
    if (this.inOnboarding) {
      return 'Subscribe';
    } else if (this.subscription) {
      const priceID = sub.PriceData.get(this.selectedBillingPeriod.ID).PriceID;
      if (sub.isHigherTier) {
        return 'Upgrade';
      } else if (!sub.isCurrent) {
        return 'Switch';
      } else {
        return 'Current Plan';
      }
    }

    return 'Switch';
  }

  getMonthlyPrice(sub: PlanCard): number {
    const monthlyPeriod = this.billingPeriods.find((period) => period.Name === 'Monthly');
    if (monthlyPeriod) {
      return sub.PriceData.get(monthlyPeriod.ID).Price;
    }
    return sub.PriceData.get(this.billingPeriods[0].ID).Price; // Fallback to the first available period
  }

  getAnnualPrice(sub: PlanCard): number {
    const annualPeriod = this.billingPeriods.find((period) => period.Name === 'Annual');
    if (annualPeriod) {
      return sub.PriceData.get(annualPeriod.ID).Price;
    }
    return sub.PriceData.get(this.billingPeriods[0].ID).Price; // Fallback to the first available period
  }

  SetOnSelectionCallback(callback: (sub: PlanCard, cycleID: string) => Promise<void>): void {
    this.onSelectionCallback = callback;
  }

  async selectPlan(sub: PlanCard): Promise<void> {
    if (this.onSelectionCallback) {
      await this.onSelectionCallback(sub, this.selectedBillingPeriod.ID);
    }
  }
}
